import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useChangePricingPlan } from 'src/features/stay/api/useChangePricingPlan';
import { LIST_ACTIVE_STAY_PERIODS_RESOURCE } from 'src/features/stay/api/useListActiveStayPeriods';
import { LIST_PAST_STAY_PERIODS_RESOURCE } from 'src/features/stay/api/useListPastStayPeriods';
import ChangeActiveStayPricingPlanForm from 'src/features/stay/components/forms/ChangeActiveStayPricingPlanForm';
import { PricingPlanChangeOptions } from 'src/features/stay/types';
import {
    ChangeActiveStayPricingPlanFormSchemaValues,
    changeActiveStayPricingPlanFormSchema,
} from 'src/features/stay/utils/formSchema';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { toast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface Props<TData> extends DataTableNewEntryProps<TData> {
    stayId?: string;
    children?: React.ReactNode;
    pricingPlanChangeOptions?: PricingPlanChangeOptions;
}

export default function ChangeActiveStayPricingPlanFormModal<TData>({
    stayId = '',
    children,
    pricingPlanChangeOptions,
}: Props<TData>) {
    const { formatMessage } = useIntl();
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { parkingId } = useParking();
    const onError = useOnError();
    const { mutate, isPending } = useChangePricingPlan({
        onSuccess: () => {
            setIsModalOpen(false);
            void queryClient.invalidateQueries({ queryKey: [LIST_ACTIVE_STAY_PERIODS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_PAST_STAY_PERIODS_RESOURCE] });
            toast({
                title: formatMessage(visitorMessages.changePricingPlanMessage),
                description: formatMessage(visitorMessages.changePricingPlanMessageDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });
    const form = useForm<ChangeActiveStayPricingPlanFormSchemaValues>({
        resolver: zodResolver(changeActiveStayPricingPlanFormSchema),
        defaultValues: {},
    });

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    React.useEffect(() => {
        form.reset({});
    }, [form]);

    const onSubmit = React.useCallback(
        (values: ChangeActiveStayPricingPlanFormSchemaValues) => mutate({ parkingId, stayId, ...values }),
        [mutate, parkingId, stayId],
    );

    React.useEffect(() => {
        const permissionBasedPricingPlan = pricingPlanChangeOptions?.permission_based_pricing_plans;
        const validationTypesWithPricingPlan = pricingPlanChangeOptions?.validation_types_with_pricing_plans;

        if (permissionBasedPricingPlan?.length === 1 && !validationTypesWithPricingPlan) {
            form.setValue('pricing_plan_identity', permissionBasedPricingPlan[0].identity);
        }
        if (validationTypesWithPricingPlan?.length === 1 && !permissionBasedPricingPlan) {
            form.setValue('validation_type', validationTypesWithPricingPlan[0].id);
        }
    }, [form, pricingPlanChangeOptions]);

    return (
        <>
            <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
                <DialogTrigger asChild>{children}</DialogTrigger>
                <DialogContent>
                    <DialogHeader className="flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.MEMBER} />
                        <div>
                            <DialogTitle>{formatMessage(visitorMessages.changePricingPlan)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(visitorMessages.changePricingPlanDescription)}
                            </DialogDescription>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <ChangeActiveStayPricingPlanForm
                            form={form}
                            onSubmit={onSubmit}
                            pricingPlanChangeOptions={pricingPlanChangeOptions}
                        />
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                <FormattedMessage {...generalMessages.cancel} />
                            </Button>
                        </DialogClose>
                        <Button
                            variant="default"
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                        >
                            {isPending && (
                                <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                            )}
                            {formatMessage(visitorMessages.changePricingPlan)}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
